import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Μπορεί να καλυφθεί οποιοδήποτε tattoo;", acceptedAnswer: { "@type": "Answer", text: "Όχι πάντα με τον ίδιο τρόπο. Εξαρτάται από τη σκουρότητα, το saturation, το μέγεθος και το placement. Ένα ξεθωριασμένο tattoo δίνει περισσότερες επιλογές." } },
    { "@type": "Question", name: "Χρειάζεται laser πριν το cover up;", acceptedAnswer: { "@type": "Answer", text: "Σε πολύ βαριά κομμάτια, fading με laser ανοίγει περισσότερες επιλογές για το τελικό design και δίνει καλύτερο αισθητικό αποτέλεσμα." } },
    { "@type": "Question", name: "Πόσο μεγαλύτερο πρέπει να είναι το cover up;", acceptedAnswer: { "@type": "Answer", text: "Εξαρτάται από το υπάρχον tattoo. Δεν χρειάζεται πάντα να γίνει σημαντικά μεγαλύτερο, η σύνθεση και η στρατηγική χρήση σκιών παίζουν μεγαλύτερο ρόλο." } },
  ],
};

export default function TattooCoverUpAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Tattoo Cover Up στην Αθήνα: πότε γίνεται και πώς",
      "description": "Πότε μπορεί να καλυφθεί ένα παλιό tattoo, τι κάνει ένα cover up πετυχημένο και πότε χρειάζεται fading πρώτα. Οδηγός από το 210 Tattoo Athens.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-04-24",
      "dateModified": "2026-04-24",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/tattoo-cover-up-athens",
      "inLanguage": "el"
    };
    inject('schema-cover-up-faq', faqSchema);
    inject('schema-cover-up-article', articleSchema);
    return () => {
      document.getElementById('schema-cover-up-faq')?.remove();
      document.getElementById('schema-cover-up-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Tattoo Cover Up στην Αθήνα: Πότε Γίνεται και Πώς | 210 Tattoo Athens</title>
        <meta name="description" content="Πότε μπορεί να καλυφθεί ένα παλιό tattoo, τι κάνει ένα cover up πετυχημένο και πότε χρειάζεται fading πρώτα. Οδηγός από το 210 Tattoo Athens." />
        <link rel="canonical" href="https://210tattoo.gr/blog/tattoo-cover-up-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/tattoo-cover-up-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/tattoo-cover-up-athens" />
        <meta property="og:title" content="Tattoo Cover Up στην Αθήνα: Πότε Γίνεται και Πώς | 210 Tattoo Athens" />
        <meta property="og:description" content="Πότε μπορεί να καλυφθεί ένα παλιό tattoo, τι κάνει ένα cover up πετυχημένο και πότε χρειάζεται fading πρώτα." />
        <meta property="og:url" content="https://210tattoo.gr/blog/tattoo-cover-up-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('tattoo-cover-up-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Tattoo Cover Up στην Αθήνα | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Πότε και πώς γίνεται ένα cover up tattoo στην Αθήνα. Οδηγός από το 210 Tattoo Athens." />
        <meta name="twitter:image" content={ogImage('tattoo-cover-up-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Cover Up Tattoo Αθήνα</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Οδηγός για cover up</span>
              <br />
              <span className="text-white">τατουάζ στην Αθήνα</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">24 Απριλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Ένα παλιό τατουάζ που δεν σε εκφράζει πια δεν σημαίνει ότι πρέπει να μείνει για πάντα όπως είναι. Πολλοί έρχονται για cover up επειδή το αρχικό σχέδιο έγινε βιαστικά, δεν εκτελέστηκε σωστά ή απλώς δεν τους αντιπροσωπεύει πια. Ένα καλό cover up είναι επανασχεδιασμός, όχι απλή κάλυψη.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Φωτογραφία για Αξιολόγηση
            </a>
          </div>
        </section>

        {/* When can it be covered */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πότε μπορεί να καλυφθεί <span className="text-pink-500">ένα παλιό tattoo</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Δεν καλύπτονται όλα με τον ίδιο τρόπο. Το αν μπορεί να γίνει cover up και με ποιες επιλογές εξαρτάται από πολλούς παράγοντες που πρέπει να αξιολογηθούν μαζί.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Σκουρότητα του υπάρχοντος τατουάζ', desc: 'Όσο πιο σκούρο, τόσο πιο δύσκολη η κάλυψη. Ένα πολύ βαθύ μαύρο μπορεί να περιορίσει σημαντικά τις επιλογές design.' },
                { title: 'Saturation', desc: 'Ένα τατουάζ με έντονο, πυκνό χρώμα απαιτεί διαφορετική προσέγγιση από ένα ξεθωριασμένο. Αν το χρώμα έχει βγει ομοιόμορφα, δίνει καλύτερη βάση.' },
                { title: 'Μέγεθος', desc: 'Ένα μεγάλο τατουάζ χρειάζεται μεγαλύτερο cover up. Αυτό δεν είναι πρόβλημα από μόνο του, αλλά πρέπει να μπαίνει στο σχεδιαστικό πλάνο από την αρχή.' },
                { title: 'Placement στο σώμα', desc: 'Κάποιες περιοχές δίνουν περισσότερο χώρο για σύνθεση. Άλλες, λόγω σχήματος ή ορατότητας, θέτουν περιορισμούς.' },
                { title: 'Πόσο healed είναι', desc: 'Ένα ξεθωριασμένο παλιό tattoo συνήθως δίνει πολύ περισσότερες επιλογές. Αν το υπάρχον είναι πολύ φρέσκο, ίσως αξίζει να περιμένεις.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What makes a cover up successful */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι κάνει ένα cover up <span className="text-pink-500">πετυχημένο</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Το μεγαλύτερο λάθος είναι να σκέφτεται κάποιος ότι απλώς κρύβεις κάτι. Στην πραγματικότητα σχεδιάζεις νέο έργο που ενσωματώνει ή εξαφανίζει το παλιό. Αυτό απαιτεί διαφορετική σκέψη από ένα συνηθισμένο tattoo.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Ένα καλό cover up πρέπει να μοιάζει σαν tattoo που σχεδιάστηκε έτσι από την αρχή. Αν φαίνεται σαν κάτι που απλώς κάλυψε κάτι άλλο, δεν έγινε σωστά.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Σωστή σύνθεση', desc: 'Το νέο σχέδιο πρέπει να μελετηθεί ώστε να ενσωματώνει ή να εξαφανίζει το παλιό με φυσικό τρόπο.' },
                { title: 'Στρατηγική χρήση σκιών', desc: 'Οι σκιές είναι το κύριο εργαλείο σε ένα cover up. Δεν χρειάζεται πάντα πυκνό μαύρο, χρειάζεται στρατηγική τοποθέτηση.' },
                { title: 'Ροή πάνω στο σώμα', desc: 'Το νέο σχέδιο πρέπει να δουλεύει με το σώμα, όχι ενάντια σε αυτό. Η ροή της σύνθεσης επηρεάζει πολύ το τελικό αποτέλεσμα.' },
                { title: 'Κατάλληλο θέμα για κάλυψη', desc: 'Δεν δουλεύει κάθε concept για cover up. Κάποια θέματα λειτουργούν καλύτερα για κάλυψη λόγω δομής και πυκνότητας.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cover up vs redesign */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Cover up versus <span className="text-pink-500">redesign</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Μερικές φορές δεν χρειάζεται πλήρες cover up. Μπορεί να αρκεί redesign ή rework. Αυτή είναι μια διάκριση που αξίζει να συζητηθεί στο consultation.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Πλήρες cover up', desc: 'Απαιτείται όταν το υπάρχον tattoo είναι αισθητικά προβληματικό και δεν μπορεί να αναβαθμιστεί. Το παλιό σχεδόν εξαφανίζεται κάτω από το νέο.' },
                { title: 'Redesign / rework', desc: 'Αν το αρχικό tattoo έχει καλή βάση αλλά χρειάζεται αναβάθμιση, ίσως αυτή είναι καλύτερη λύση. Λιγότερο invasive και μπορεί να δώσει εξαιρετικό αποτέλεσμα.' },
                { title: 'Partial cover up', desc: 'Κάποιες φορές μόνο τμήμα του υπάρχοντος tattoo χρειάζεται κάλυψη. Ο υπόλοιπος μπορεί να ενταχθεί στη νέα σύνθεση.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* When fading/laser is needed first */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πότε χρειάζεται fading <span className="text-pink-500">ή laser πρώτα</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Σε κάποια πολύ βαριά κομμάτια ίσως χρειάζεται πρώτα lightening. Αυτό δεν είναι αποτυχία, είναι στρατηγική.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Μερικές συνεδρίες laser fading ανοίγουν πολύ περισσότερες επιλογές για το τελικό design και συχνά δίνουν καλύτερο αισθητικό αποτέλεσμα από το να προσπαθήσεις να κάνεις cover up σε ένα βαρύ τατουάζ απευθείας.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Πολύ σκούρο ή πυκνό μελάνι', desc: 'Αν το υπάρχον tattoo έχει πολύ βαθύ μαύρο ή έντονο χρώμα, το fading ανοίγει τη βάση για πιο ελεύθερη σύνθεση.' },
                { title: 'Θέλεις να κρατήσεις επιλογές', desc: 'Αν δεν είσαι σίγουρος για το νέο design, το fading δίνει χρόνο να αποφασίσεις χωρίς να περιορίζεσαι.' },
                { title: 'Περιορισμένος χώρος για κάλυψη', desc: 'Αν το υπάρχον tattoo είναι σε placement που δεν επιτρέπει πολύ μεγαλύτερο νέο σχέδιο, το fading βοηθά να παραμείνει στο ίδιο μέγεθος περίπου.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Common mistakes */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Συχνά λάθη <span className="text-pink-500">στα cover ups</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Βλέπουμε αρκετά cover ups που δεν πέτυχαν, όχι επειδή δεν έγιναν με καλές προθέσεις, αλλά επειδή δεν υπήρξε σωστό σχεδιαστικό πλάνο.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Υπερβολικά μαύρα', desc: 'Το να γεμίσεις μαύρο δεν είναι λύση. Είναι ο πιο βαρύς τρόπος να κάνεις cover up και συχνά δίνει αποτέλεσμα που δεν ικανοποιεί.' },
                { title: 'Δεν κρύβουν όντως το παλιό σχέδιο', desc: 'Αν μετά από μερικούς μήνες το παλιό tattoo αρχίζει να φαίνεται μέσα από το νέο, η κάλυψη δεν έγινε σωστά.' },
                { title: 'Γίνονται πολύ μεγαλύτερα χωρίς λόγο', desc: 'Το cover up δεν απαιτεί πάντα να επεκταθείς σημαντικά. Σωστή σύνθεση μπορεί να πετύχει κάλυψη σε λογικά όρια μεγέθους.' },
                { title: 'Θυσιάζουν αισθητική μόνο για κάλυψη', desc: 'Το τελικό αποτέλεσμα πρέπει να είναι κάτι που θέλεις, όχι απλώς κάτι που κρύβει. Αν δεν σου αρέσει το νέο design, δεν έχεις κερδίσει τίποτα.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 210 approach */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Cover up λύσεις στο <span className="text-pink-500">210 Tattoo Athens</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Στο 210 Tattoo Athens αντιμετωπίζουμε τα cover ups σαν design challenge. Δεν υπάρχει standard λύση, κάθε υπάρχον tattoo απαιτεί ξεχωριστή αξιολόγηση και στρατηγική.
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Στέλνεις φωτογραφία του υπάρχοντος tattoo', desc: 'Ζητάμε φωτογραφία πριν consultation ώστε να αξιολογήσουμε επιλογές. Χρειαζόμαστε να δούμε τη βάση που δουλεύουμε.' },
                { step: '02', title: 'Αξιολόγηση επιλογών', desc: 'Εκτιμούμε αν χρειάζεται fading πρώτα, τι εύρος σχεδίων μπορεί να δουλέψει και τι είναι ρεαλιστικό για το συγκεκριμένο tattoo.' },
                { step: '03', title: 'Σχεδιαστικό πλάνο', desc: 'Αναπτύσσουμε concept που ενσωματώνει ή εξαφανίζει το παλιό, με στόχο να μοιάζει σαν tattoo που σχεδιάστηκε έτσι από την αρχή.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Σκέφτεσαι <span className="text-pink-500">cover up;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Αν σκέφτεσαι cover up, στείλε μας φωτογραφία στο Instagram του 210 Tattoo Athens για πρώτη αξιολόγηση. Θα σου πούμε ειλικρινά τι είναι εφικτό και με ποιες επιλογές.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Φωτογραφία για Αξιολόγηση
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-artist-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς να διαλέξεις tattoo artist στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι να προσέξεις πριν επιλέξεις studio: portfolio, hygiene, consultation και red flags.</p>
              </Link>
              <Link to="/blog/laser-vs-cover-up-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Λέιζερ ή Cover Up;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πότε αξίζει λέιζερ αφαίρεση και πότε cover up, και πότε χρειάζεται συνδυασμός.</p>
              </Link>
              <Link to="/blog/poia-tatouaz-kaliptontai-cover-up-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Ποια Τατουάζ Καλύπτονται πιο Εύκολα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Οι παράγοντες που καθορίζουν την ευκολία ενός cover up.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/blackwork-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Blackwork Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
